/**
 * Centralized constants for the Community Marketplace feature
 * Non-string configuration values: limits, sizes, timeouts, etc.
 */

// ============================================================================
// Layout & Grid Constants
// ============================================================================

/**
 * Maximum content width for responsive layouts
 */
export const MARKETPLACE_MAX_CONTENT_WIDTH = {
  mobile: undefined, // Full width
  tablet: 720,
  desktop: 960,
} as const;

/**
 * Grid column counts by layout mode
 */
export const MARKETPLACE_GRID_COLUMNS = {
  mobile: 2,
  tablet: 3,
  desktop: 4,
} as const;

/**
 * Grid gap (spacing between items) by layout mode
 */
export const MARKETPLACE_GRID_GAP = {
  mobile: 15,
  tablet: 20,
  desktop: 24,
} as const;

/**
 * Content padding by layout mode
 */
export const MARKETPLACE_CONTENT_PADDING = {
  mobile: 20,
  tablet: 32,
  desktop: 40,
} as const;

/**
 * Mobile frame padding (gradient border)
 */
export const MARKETPLACE_FRAME_PADDING = {
  outer: 3,
  middle: 2,
  inner: 1,
  total: 12, // (3 + 2 + 1) * 2
} as const;

// ============================================================================
// Image & Media Constants
// ============================================================================

/**
 * Maximum number of photos per listing
 */
export const MAX_LISTING_PHOTOS = 10;

/**
 * Image upload constraints
 */
export const IMAGE_UPLOAD = {
  maxCount: 10,
  maxSizeMB: 10,
  maxSizeBytes: 10 * 1024 * 1024,
  supportedFormats: ['image/jpeg', 'image/png', 'image/webp'],
  defaultQuality: 0.8,
} as const;

/**
 * Image carousel dimensions
 */
export const CAROUSEL_DIMENSIONS = {
  mobile: {
    aspectRatio: 0.9,
  },
  tablet: {
    maxWidth: 500,
    aspectRatio: 0.85,
  },
  desktop: {
    maxWidth: 560,
    aspectRatio: 0.8,
  },
} as const;

// ============================================================================
// Input Limits
// ============================================================================

/**
 * Text input character limits
 */
export const INPUT_LIMITS = {
  title: 100,
  description: 500,
  message: 1000,
  reportDescription: 500,
  reviewText: 500,
} as const;

// ============================================================================
// Listing Constants
// ============================================================================

/**
 * Listing condition values
 */
export const LISTING_CONDITIONS = ['new', 'new_without_tags', 'like_new', 'used', 'fair'] as const;
export type ListingCondition = typeof LISTING_CONDITIONS[number];

/**
 * Post item condition options (subset for form)
 */
export const POST_ITEM_CONDITIONS = ['brand_new', 'new_without_tags', 'used'] as const;
export type PostItemCondition = typeof POST_ITEM_CONDITIONS[number];

/**
 * Pickup options
 */
export const PICKUP_OPTIONS = ['room_pickup', 'lobby_pickup', 'any_pickup'] as const;
export type PickupOption = typeof PICKUP_OPTIONS[number];

/**
 * Post item pickup options (multi-select for form)
 */
export const POST_ITEM_PICKUP_OPTIONS = ['room_pickup', 'lobby_pickup', 'meet_in_person'] as const;
export type PostItemPickupOption = typeof POST_ITEM_PICKUP_OPTIONS[number];

/**
 * Listing status values
 */
export const LISTING_STATUSES = ['active', 'sold', 'removed'] as const;
export type ListingStatus = typeof LISTING_STATUSES[number];

/**
 * Report reason values
 */
export const REPORT_REASONS = ['spam', 'inappropriate', 'harassment'] as const;
export type ReportReason = typeof REPORT_REASONS[number];

// ============================================================================
// Pagination & Limits
// ============================================================================

/**
 * API pagination settings
 */
export const PAGINATION = {
  defaultPageSize: 25,
  recentlyViewedLimit: 10,
  maxRecentlyViewed: 20,
  inboxPageSize: 20,
} as const;

// ============================================================================
// Rating Constants
// ============================================================================

/**
 * Rating configuration
 */
export const RATING = {
  min: 1,
  max: 5,
  defaultEmpty: 0,
} as const;

// ============================================================================
// Animation & Timing
// ============================================================================

/**
 * Animation durations (in milliseconds)
 */
export const ANIMATION = {
  fast: 150,
  normal: 300,
  slow: 500,
  carouselAutoPlay: 5000,
} as const;

/**
 * Debounce delays (in milliseconds)
 */
export const DEBOUNCE = {
  search: 300,
  scroll: 100,
  input: 150,
} as const;

// ============================================================================
// Post Item Flow
// ============================================================================

/**
 * Post item step order
 */
export const POST_ITEM_STEPS = ['details', 'media', 'price', 'pickup', 'success'] as const;
export type PostItemStep = typeof POST_ITEM_STEPS[number];

/**
 * Total number of steps in post item flow
 */
export const POST_ITEM_TOTAL_STEPS = POST_ITEM_STEPS.length;

// ============================================================================
// Profile Navigation Options
// ============================================================================

/**
 * Profile dropdown navigation options
 */
export const PROFILE_NAV_OPTIONS = [
  { id: 'my_profile', label: 'My profile', route: 'MarketplaceProfile' },
  { id: 'saved_items', label: 'Saved items', route: 'SavedListings' },
  { id: 'inbox', label: 'Inbox', route: 'MarketplaceInbox' },
  { id: 'following', label: 'Following', route: 'MarketplaceFollowing' },
] as const;

export type ProfileNavOptionId = typeof PROFILE_NAV_OPTIONS[number]['id'];

// ============================================================================
// Cache Tags (for RTK Query)
// ============================================================================

/**
 * RTK Query cache tag types
 */
export const CACHE_TAGS = {
  Listing: 'Listing',
  MyListings: 'MyListings',
  SavedItems: 'SavedItems',
  RecentlyViewed: 'RecentlyViewed',
  Profile: 'Profile',
  SellerProfile: 'SellerProfile',
  Following: 'Following',
  Followers: 'Followers',
  Reviews: 'Reviews',
  Conversations: 'Conversations',
  Conversation: 'Conversation',
  FeedbackRequests: 'FeedbackRequests',
  MarketplaceNotifications: 'MarketplaceNotifications',
} as const;

// ============================================================================
// API Endpoints
// ============================================================================

/**
 * Base marketplace API path
 */
export const MARKETPLACE_API_BASE = '/api/v1/marketplace';

/**
 * Marketplace API endpoint paths
 */
export const MARKETPLACE_API = {
  // Listings
  listings: {
    base: `${MARKETPLACE_API_BASE}/listings`,
    byId: (id: string) => `${MARKETPLACE_API_BASE}/listings/${id}`,
    sold: (id: string) => `${MARKETPLACE_API_BASE}/listings/${id}/sold`,
    repost: (id: string) => `${MARKETPLACE_API_BASE}/listings/${id}/repost`,
    buyers: (id: string) => `${MARKETPLACE_API_BASE}/listings/${id}/buyers`,
    conversations: (id: string) => `${MARKETPLACE_API_BASE}/listings/${id}/conversations`,
  },
  
  // Profile
  profile: {
    base: `${MARKETPLACE_API_BASE}/profile`,
    byUserId: (userId: string) => `${MARKETPLACE_API_BASE}/profile/${userId}`,
    myListings: `${MARKETPLACE_API_BASE}/profile/my-listings`,
    saved: `${MARKETPLACE_API_BASE}/profile/saved`,
    savedById: (listingId: string) => `${MARKETPLACE_API_BASE}/profile/saved/${listingId}`,
    recentlyViewed: `${MARKETPLACE_API_BASE}/profile/recently-viewed`,
  },
  
  // Social
  social: {
    following: `${MARKETPLACE_API_BASE}/social/following`,
    followers: `${MARKETPLACE_API_BASE}/social/followers`,
    follow: (userId: string) => `${MARKETPLACE_API_BASE}/social/follow/${userId}`,
    reviews: `${MARKETPLACE_API_BASE}/social/reviews`,
  },
  
  // Messages
  messages: {
    conversations: `${MARKETPLACE_API_BASE}/messages/conversations`,
    conversationById: (id: string) => `${MARKETPLACE_API_BASE}/messages/conversations/${id}`,
    conversationByListing: (listingId: string) => `${MARKETPLACE_API_BASE}/messages/conversations/listing/${listingId}`,
    sendMessage: (conversationId: string) => `${MARKETPLACE_API_BASE}/messages/conversations/${conversationId}/messages`,
    markAsRead: (conversationId: string) => `${MARKETPLACE_API_BASE}/messages/conversations/${conversationId}/read`,
  },
  
  // Reports
  reports: {
    base: `${MARKETPLACE_API_BASE}/reports`,
  },
  
  // Feedback Requests
  feedbackRequests: {
    base: `${MARKETPLACE_API_BASE}/feedback-requests`,
    pending: `${MARKETPLACE_API_BASE}/feedback-requests/pending`,
    complete: (id: string) => `${MARKETPLACE_API_BASE}/feedback-requests/${id}/complete`,
  },
  
  // Marketplace Notifications
  notifications: {
    base: `${MARKETPLACE_API_BASE}/notifications`,
    markAsRead: (id: string) => `${MARKETPLACE_API_BASE}/notifications/${id}/read`,
    delete: (id: string) => `${MARKETPLACE_API_BASE}/notifications/${id}`,
  },
} as const;
